import { MultiSelect } from 'primereact/multiselect';
import { Dropdown } from 'primereact/dropdown';
import { useDispatch, useSelector } from 'react-redux';
import { CopyToClipboard, StatusSelector, BaseChip, BaseCollectionTypeSelector, BaseTabsMenu, BaseInput, BaseImageUpload } from './index';
import {
  SET_COLLECTION_PROP,
  UPDATE_SUBSCRIPTIONS,
  UPDATE_ADDITIONAL_SUBSCRIPTION,
  HANDLE_PRM_API_ERR
} from '../store/modules/collection';
import './css/key-details-section.scss';
import {
  HOSTED,
  EDITORIAL,
  CUSTOM,
  PARTNER_COLLECTION,
  EDITORIAL_INDEPENDENT
} from '../utils/Constants';
import { useState, useEffect } from 'react';
import { updateCollectionImg, fetchGroupProducts } from '../store/api';

export const CollectionKeyDetails = function () {
  const {
    subscriptionsList,
    data: { id, status, title, locale, partnersLogo, accessResources, partnerName, type, partnershipType, productId, definition, collectionPrice, collectionOutlines }
  } = useSelector((state) => state.collection);

  const partnershipTypeOptions = [HOSTED, EDITORIAL_INDEPENDENT, CUSTOM]
  const [handlePartnershipType, setHandlePartnershipType] = useState(partnershipType)

  // State for Collection Subscription dropdown
  const [collectionSubscriptionList, setCollectionSubscriptionList] = useState([]);

  // State for dynamic Additional subscriptions MultiSelect
  const [dynamicSubscriptionsList, setDynamicSubscriptionsList] = useState([]);
  const [dynamicSubscriptions, setDynamicSubscriptions] = useState([]);

  useEffect(() => {
    fetchGroupProducts('grp260')
      .then((data) => {
        const items = Array.isArray(data) ? data : (data?.content ?? data?.products ?? []);
        const list = items
          .filter((p) => !p.state || p.state.toUpperCase() === 'ENABLED')
          .map((p) => ({
            id: p.id,
            displayName: p.properties?.displayName ?? p.name ?? p.id,
            code: p.properties?.definition ?? p.id
          }));
        setCollectionSubscriptionList(list);
        setDynamicSubscriptionsList(list);
      })
      .catch((err) => {
        console.error('fetchGroupProducts error:', err);
        dispatch(HANDLE_PRM_API_ERR(true));
      });
  }, []);

  useEffect(() => {
    if (!dynamicSubscriptionsList.length) return;
    setDynamicSubscriptions(
      dynamicSubscriptionsList.filter((item) =>
        (accessResources ?? []).some((r) =>
          (r.code && r.code === item.code) || String(r.id) === String(item.id)
        )
      )
    );
  }, [dynamicSubscriptionsList, accessResources]);

  const dispatch = useDispatch();

  const selectedSubTemplate = (option, props) => {
    if (option) {
      return (
        <div className="flex align-items-center">
          <div>{option.displayName}</div>
        </div>
      );
    }

    return <span>{props.placeholder}</span>;
  };

  const subOptionTemplate = (option) => {
    return (
      <div className="flex align-items-center">
        <div>{option.displayName}</div>
      </div>
    );
  };

  useEffect(() => {
    (partnershipType === EDITORIAL ? setHandlePartnershipType(EDITORIAL_INDEPENDENT) : setHandlePartnershipType(partnershipType))
  }, [partnershipType]);

  function handlePropUpdate({ key, value }) {
    if (key === 'type' && value === PARTNER_COLLECTION && !collectionOutlines) {
      dispatch(SET_COLLECTION_PROP({ key: 'collectionOutlines', value: [] }));
    }
    dispatch(SET_COLLECTION_PROP({ key, value }));
  }

  function handleUpdateSubscriptions(arr) {
    dispatch(UPDATE_SUBSCRIPTIONS(arr));
  }

  function handleCollectionPartnershipType({ value }) {
    (value === EDITORIAL ? setHandlePartnershipType(EDITORIAL_INDEPENDENT) : setHandlePartnershipType(value))
    switch (value) {
      case HOSTED:
        return dispatch(SET_COLLECTION_PROP({ key: 'partnershipType', value: HOSTED }));
      case EDITORIAL_INDEPENDENT:
        return dispatch(SET_COLLECTION_PROP({ key: 'partnershipType', value: EDITORIAL }));
      case CUSTOM:
        return dispatch(SET_COLLECTION_PROP({ key: 'partnershipType', value: CUSTOM }));

      default:
        dispatch(SET_COLLECTION_PROP({ key: 'partnershipType', value: '' }));
        break;
    }

  }

  async function handleImage({ key, action, imageName, formData }) {
    switch (action) {
      case 'upload':
        dispatch(
          SET_COLLECTION_PROP({
            key,
            value: key === 'partnersLogo' ? [imageName] : imageName
          })
        );
        await updateCollectionImg(formData);
        break;
      case 'delete':
        dispatch(
          SET_COLLECTION_PROP({
            key,
            value: key === 'partnersLogo' ? null : ''
          })
        );
        break;
    }
  }

  function handleInput({ key, value }) {
    dispatch(SET_COLLECTION_PROP({ key, value }));
  }
  const handleKeyPress = (event) => {
    const charCode = event.which || event.keyCode;
    // Allow control keys (backspace, etc. charCode < 32)
    // Allow digits (48-57) and period (46)
    if (charCode > 31 && (charCode < 48 || charCode > 57) && charCode !== 46) {
      event.preventDefault();
    }
  };

  return (
    <section className="key-details-section" id="key-details">
      <header className="flex flex-between">
        <h2>Key details</h2>
        <div className="flex-center">
          <BaseCollectionTypeSelector value={type} onChange={handlePropUpdate} />
          <StatusSelector name="status" status={status} onChange={handlePropUpdate} />
        </div>
      </header>
      {type === PARTNER_COLLECTION && <div className='m-b-24'>
        <BaseTabsMenu
          name='partnershipType'
          label="Partnership type"
          value={handlePartnershipType}
          options={partnershipTypeOptions}
          onSelect={handleCollectionPartnershipType}
        />
      </div>}

      <div>
        <label htmlFor="title">
          Collection title
          <br />
          <textarea
            className="w100"
            cy="collection-title"
            id="title"
            value={title}
            onInput={(ev) => handlePropUpdate({ key: 'title', value: ev.target.value })}
            placeholder="collection title"
          />
        </label>
        <span>Locale: {locale}</span>
      </div>

      <div className="unique-updated flex m-t-24">
        <div className="m-r-24">
          <label htmlFor="unique">
            Unique ID <br />
            <input
              type="text"
              cy="course-id"
              id="unique"
              value={id || ''}
              disabled
              onInput={() => { }}
            />
          </label>
          <CopyToClipboard />
        </div>
      </div>

      <div className="subscriptions-chips m-t-24" cy="subscriptions">
        <b>Subscriptions</b>
        <br />
        {subscriptionsList?.map(({ displayName, id }) => (
          <BaseChip
            label={displayName}
            id={id}
            key={id}
            selected={accessResources?.map((o) => o.id).some((val) => +val === +id)}
            section="keyDetails"
            onClick={handleUpdateSubscriptions}
          />
        ))}
      </div>
      <div id="dynamic-subscriptions" className="relative m-t-24">
        <label htmlFor="dynamic" cy="dynamic-subscriptions">
          Additional subscriptions <br />
          <MultiSelect
            inputId="dynamic"
            value={dynamicSubscriptions}
            options={dynamicSubscriptionsList}
            onChange={(e) => {
              setDynamicSubscriptions(e.value);
              dispatch(UPDATE_ADDITIONAL_SUBSCRIPTION(e.value));
            }}
            optionLabel="displayName"
            placeholder="Search all other subscriptions"
            display="chip"
            filter
            resetFilterOnHide
          />
        </label>
      </div>
      {type === PARTNER_COLLECTION && partnershipType === HOSTED && <>
        <div className="relative m-t-24">
          <label>Collection Subscription</label><br />
          <Dropdown
            value={definition}
            onChange={(e) => {
              const selected = collectionSubscriptionList.find(c => c.code === e.value);
              dispatch(SET_COLLECTION_PROP({ key: 'definition', value: e.value }));
              dispatch(SET_COLLECTION_PROP({ key: 'productId', value: selected?.id ?? '' }));
            }}
            options={collectionSubscriptionList}
            optionLabel="displayName"
            optionValue="code"
            placeholder="Select Collection subscription"
            filter
            filterDelay={400}
            valueTemplate={selectedSubTemplate}
            itemTemplate={subOptionTemplate}
            className="w-full md:w-14rem"
          />

        </div>
        <div className="unique-updated flex m-t-24">
          <div className="m-r-24">
            <label htmlFor="stripeProductID">
              Stripe Product ID <br />
              <input
                type="text"
                id="stripeProductID"
                className="product-id"
                value={productId || ''}
                disabled
                onInput={() => { }}
              />
            </label>
          </div>
        </div>
      </>
      }
      {type === PARTNER_COLLECTION && partnershipType === HOSTED && <div className="m-t-24 m-r-24">
        <b>Price</b>
        <br />
        <label htmlFor={'price'} className=" m-t-24 price-input">
          <span>{'£'}</span>
          <input
            name='price'
            type="text"
            id='price'
            value={!collectionPrice ? '' : collectionPrice}
            onKeyPress={handleKeyPress}
            onInput={(ev) => handleInput({ key: 'collectionPrice', value: ev.target.value })}
          />
        </label>
      </div>}
      {type === PARTNER_COLLECTION && <>
        <div className="partner-name m-r-24 m-t-24 m-b-24">
          <BaseInput
            name='partnerName'
            label="Partner name"
            value={partnerName}
            onInput={({ value }) => handlePropUpdate({ key: 'partnerName', value })}
          />
        </div>
        <BaseImageUpload
          name="partnersLogo"
          label="Partner logo"
          imageName={partnersLogo && partnersLogo[0]}
          handleImage={handleImage}
        />
      </>
      }
    </section>
  );
};
